/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DialogTutorialsComponent } from '@app/core/header/tutorials/dialog-tutorials/dialog-tutorials.component';
import { currentClusterInfo, dashboardsByGroupId, selectUrl } from '@app/core/ngrx/app.selectors';
import { AppState, Dashboard, Tutorial, TutorialStep } from '@app/core/types';
import { MONITORING_DASHBOARD_GROUP_NAME } from '@app/monitoring-dashboard/constants/dashboard-id';
import { Store } from '@ngrx/store';
import { ClusterInfo } from '@shared/types/cluster';
import type Driver from 'driver.js';
import { Observable, firstValueFrom, map } from 'rxjs';
import { tutorialCompletedAction } from '../ngrx/actions/tutorial.actions';

@Injectable({
  providedIn: 'root',
})
export class TutorialsService {
  constructor(
    public router: Router,
    private matDialog: MatDialog,
    private store: Store<AppState>,
  ) {}

  private driver?: Driver;

  public currentClusterInfo$: Observable<ClusterInfo | undefined> = this.store.select(currentClusterInfo);
  public dashboards$: Observable<(Dashboard | undefined)[]> = this.store.select(
    dashboardsByGroupId(MONITORING_DASHBOARD_GROUP_NAME),
  );

  private tutorials: Tutorial<Driver.Step>[] = [
    {
      id: `welcome-new-user`,
      title: 'New user',
      description: 'Short description of this tutorial',
      image: `welcome-new-user`,
      onStart: async () => {
        const routeClusterId = (await firstValueFrom(this.currentClusterInfo$))?.id;
        if (!this.router.url.includes(`clusters/${routeClusterId}/monitoring-dashboard`)) {
          await this.router.navigate(['clusters', routeClusterId, 'monitoring-dashboard']);
        }
        // check if there's no way to get dashboards before cluster loaded
        this.router.navigate(['clusters', routeClusterId, 'monitoring-dashboard/my-cluster']).then(async () => {
          setTimeout(async () => {
            if (!this.tutorials[0]) return;
            this.driver?.defineSteps(this.tutorials[0].tutorial);
            this.driver?.start(0);
          }, 0);
        });
      },

      tutorial: [
        {
          element: 'widgets-dashboard [gta="routed_tab"][data-tab-index="0"]',
          popover: {
            title: 'My Cluster',
            description: 'View up-to-date information about your cluster and track the general cluster health.',
            position: 'right',
          },
        },
        {
          element: 'widget-connections',
          popover: {
            title: 'Connections',
            description:
              'Connect your application to the cluster. Select the connection template and follow the instructions.',
            position: 'right',
          },
          onNext: async () => {
            this.driver?.preventMove();
            if (!this.tutorials[0]) return;
            // that's a hack to stop step transition until route will finish it's transition
            const dashboards = await firstValueFrom(this.dashboards$);
            const routeClusterId = (await firstValueFrom(this.currentClusterInfo$))?.id;
            if (dashboards.length > 1) {
              const dashboard = dashboards.find((dashboard) => dashboard && dashboard.widgets!.length);
              if (dashboard && dashboard.id)
                this.router.navigate(['clusters', routeClusterId, 'monitoring-dashboard', dashboard.id]).then(() => {
                  setTimeout(async () => {
                    this.driver?.defineSteps(this.tutorials[0]!.tutorial);
                    this.driver?.start(1);
                  }, 0);
                });
            } else {
              this.driver?.moveNext();
            }
          },
        },
        {
          element: 'widgets-dashboard [gta="routed_tab"][data-tab-index="1"]',
          popover: {
            title: 'Dashboard',
            description: 'View and modify cluster metrics in pre-designed <b>dashboards</b>.',
            // 'Templates define dashboards that display vital cluster metrics. You can apply an existing template “as is,” modify an existing template, or create your own template.',
            position: 'right',
          },
          onNext: async () => {
            if (!this.tutorials[0]) return;
            this.driver?.preventMove();
            this.driver?.defineSteps(this.tutorials[0].tutorial);
            this.driver?.start(2);
          },
          onPrevious: async () => {
            this.driver?.preventMove();
            if (!this.tutorials[0]) return;
            const dashboards = await firstValueFrom(this.dashboards$);
            const routeClusterId = (await firstValueFrom(this.currentClusterInfo$))?.id;
            if (dashboards.length) {
              const dashboard = dashboards.find((dashboard) => dashboard && dashboard.widgets!.length);
              if (dashboard && dashboard.id)
                this.router.navigate(['clusters', routeClusterId, 'monitoring-dashboard', 'my-cluster']).then(() => {
                  setTimeout(async () => {
                    this.driver?.defineSteps(this.tutorials[0]!.tutorial);
                    this.driver?.start(1);
                  }, 0);
                });
            }
          },
        },
        {
          element: 'widgets-dashboard ktd-grid-item:first-of-type',
          popover: {
            title: 'Widgets',
            description:
              'Widgets visualize the cluster metrics as tables, charts, heat maps, etc. You can configure sizes, layouts, etc., to optimize the visualization for your needs.',
            position: 'auto',
          },
        },
        {
          element: 'mat-nav-list [aria-label="Queries"]',
          popover: {
            title: 'Queries',
            description:
              'Execute an SQL query to check your app connection to the cluster. Execute a sample pre-populated SQL query and check the result. Define and run your own query. <a target="_blank" href="https://www.gridgain.com/docs/nebula/managing/sql/querying">Learn more...</a>',
            position: 'right',
          },
        },
        {
          element: 'mat-nav-list [aria-label="Compute"]',
          popover: {
            title: 'Compute',
            description:
              'Monitor сompute tasks executed on your cluster, prioritize and configure them to match your needs.',
            position: 'right',
          },
        },
        {
          element: 'mat-nav-list [aria-label="Tracing"]',
          popover: {
            title: 'Tracing',
            description:
              'View traces recorded during cluster operation - informational messages, error messages, stack traces, etc. Configure trace display. Filter traces to quickly find the ones you are looking for.',
            position: 'right',
          },
          onNext: async () => {
            // that's a hack to stop step transition until route will finish it's transition
            this.matDialog?.open(DialogTutorialsComponent, {
              data: {
                description: 'This concludes the tour. You are now all set to manage your clusters. Enjoy!',
                dismissButtonLabel: 'Finish',
              },
              width: '560px',
              position: { top: '50px' },
            });
          },
        },
      ],
      routes: [],
    },
  ];

  private tutorialsCurrent$: Observable<Tutorial<Driver.Step>[]> = this.store.select(selectUrl).pipe(
    map((url) => {
      if (url) {
        return this.tutorials.filter((tutorial: Tutorial<Driver.Step>) =>
          tutorial.routes.some(
            (route: string) =>
              url
                .split('/')
                .filter((routeQuantum: string) => routeQuantum)
                .includes(route) || route === 'all',
          ),
        );
      }
      return [];
    }),
  );

  public getAll() {
    return this.tutorials;
  }

  public getCurrent() {
    return this.tutorialsCurrent$;
  }

  public async openTutorial(id: string) {
    if (typeof this.driver !== 'undefined') delete this.driver;
    await import('driver.js')
      .then((Driver) => {
        this.driver = new Driver.default({
          className: 'scoped-class', // className to wrap driver.js popover
          animate: true, // Animate while changing highlighted element
          opacity: 0.5, // Background opacity (0 means only popovers and without overlay)
          padding: 10, // Distance of element from around the edges
          allowClose: false, // Whether clicking on overlay should close or not
          overlayClickNext: false, // Should it move to next step on overlay click
          doneBtnText: 'Done', // Text on the final button
          closeBtnText: 'End tour', // Text on the close button for this step
          nextBtnText: '&rarr;', // Next button text for this step
          prevBtnText: '&larr;', // Previous button text for this step
          showButtons: true, // Do not show control buttons in footer
          keyboardControl: true, // Allow controlling through keyboard (escape to close, arrow keys to move)
          scrollIntoViewOptions: {}, // We use `scrollIntoView()` when possible, pass here the options for it if you want any,
          onReset: () => {
            this.store.dispatch(tutorialCompletedAction({ id }));
          },
        });
      })
      .catch((err) => {
        console.log('Error loading Driver.js', err);
      });

    const startTutorial = (steps: TutorialStep<Driver.Step>[]) => {
      if (this.driver) {
        this.driver.defineSteps(steps);
        this.driver.start();
      }
    };
    setTimeout(async () => {
      const tutorial: Tutorial<Driver.Step> | undefined = this.tutorials.find((tutorial) => id === tutorial.id);
      if (tutorial) {
        if (tutorial.onStart) await tutorial.onStart();
        startTutorial(tutorial.tutorial);
      } else console.error(`There's no tutorial with "`, id, `"`);
    }, 0);
  }
}
